<script setup lang="ts">
import type { DateRangePickerFieldValue } from '@/components/pickerFields/DateRangePickerField.vue';

import { reactive, ref, toRaw } from 'vue';

import { arJobStatuses } from '@/utils/PortalConstants';
import KBtn from '@/components/btns/KBtn.vue';
import CustomerCodeAutocompleteField from '@/components/fields/CustomerCodeAutocompleteField.vue';
import KRadioField from '@/components/fields/KRadioField.vue';
import KSingleAutocompleteField from '@/components/fields/KSingleAutocompleteField.vue';
import KTextField from '@/components/fields/KTextField.vue';
import DateRangePickerField from '@/components/pickerFields/DateRangePickerField.vue';
import useForm from '@/composables/useForm';
import { requiredValidationRule } from '@/helpers/validationRules';

export interface InitialData {
  dates?: DateRangePickerFieldValue | null;
  status?: number | null;
  customer?: string | null;
  search?: string | null;
  dateType?: string | null;
}

export interface ValidData {
  dates: DateRangePickerFieldValue;
  status?: number | null;
  customer?: string | null;
  search?: string | null;
  dateType: string;
}

export interface Props {
  loading?: boolean;
  initialData?: InitialData;
}
const props = withDefaults(defineProps<Props>(), {
  loading: false,
  initialData: () => ({}),
});
const emit = defineEmits<{
  submit: [formData: ValidData];
}>();

const formData = reactive<InitialData>({});
const isValid = ref(true);

const { form, handleSubmit } = useForm(props, formData, () => {
  emit('submit', toRaw(formData) as ValidData);
});

const statusItems = arJobStatuses
  .filter((item) => item.value !== 0)
  .map((item) => {
    return {
      title: item.text,
      value: item.value,
    };
  });
</script>

<template>
  <v-form
    ref="form"
    v-model="isValid"
    validate-on="lazy"
    :class="$style.bookedAirRoadSearchForm"
    data-testid="bookedAirRoadSearchForm"
    @submit.prevent="handleSubmit"
  >
    <DateRangePickerField
      v-model="formData.dates"
      size="lg"
      label="Date range"
      :disabled="props.loading"
      :rules="[requiredValidationRule]"
      :class="$style.field"
    />
    <CustomerCodeAutocompleteField
      v-model="formData.customer"
      size="large"
      :disabled="props.loading"
      :class="$style.field"
      @keydown.enter="handleSubmit"
    />
    <KSingleAutocompleteField
      v-model="formData.status"
      size="lg"
      label="Status"
      :disabled="props.loading"
      :class="$style.field"
      :items="statusItems"
      @keydown.enter="handleSubmit"
    />
    <KTextField
      v-model="formData.search"
      size="lg"
      label="Search"
      :disabled="props.loading"
      :class="$style.field"
    />
    <v-radio-group
      v-model="formData.dateType"
      :disabled="props.loading"
      :rules="[requiredValidationRule]"
      :class="$style.radioGroup"
      inline
    >
      <KRadioField label="RH job date" value="rh_job_date" />
      <KRadioField label="AR created" value="ar_created" />
      <KRadioField label="Required" value="required" />
    </v-radio-group>
    <KBtn
      type="submit"
      size="lg"
      prepend-icon="mdi-magnify"
      :loading="props.loading"
      :class="$style.submit"
    >
      Search
    </KBtn>
  </v-form>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';
@use '@/styles/variables';

.bookedAirRoadSearchForm {
}
  .field {
    width: 17.5rem;
    display: inline-block;
    vertical-align: top;
  }
  .field {
    margin-right: 1.5rem;
    margin-bottom: 0.5rem;
  }
  .radioGroup {
    margin-bottom: 0.5rem;
  }
  .submit {
    margin-bottom: calc(0.5rem + 22px);
  }
</style>
